import { useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { Camera, X, Loader2, ImagePlus } from "lucide-react";
import { toast } from "sonner";

const MAX = 6;

export default function PhotoUpload({ photos, setPhotos }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onPick = () => inputRef.current?.click();

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const room = MAX - photos.length;
    if (room <= 0) {
      toast.error(`You can upload up to ${MAX} photos.`);
      e.target.value = "";
      return;
    }
    if (files.length > room) {
      toast.warning(`Only the first ${room} photo${room > 1 ? "s" : ""} will be added — max ${MAX} total.`);
    }
    const toUpload = files.slice(0, room);
    setUploading(true);
    for (const file of toUpload) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/photos", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setPhotos((prev) => [
          ...prev,
          { id: data.id, filename: data.filename, previewUrl: URL.createObjectURL(file) },
        ]);
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Couldn't upload ${file.name}`);
      }
    }
    setUploading(false);
    e.target.value = "";
  };

  const remove = (id) => {
    setPhotos((prev) => {
      const found = prev.find((p) => p.id === id);
      if (found?.previewUrl) URL.revokeObjectURL(found.previewUrl);
      return prev.filter((p) => p.id !== id);
    });
  };

  return (
    <div data-testid="photo-upload-block">
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
        multiple
        className="hidden"
        onChange={onFiles}
        data-testid="photo-file-input"
      />
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        {photos.map((p) => (
          <div
            key={p.id}
            className="relative aspect-square rounded-xl overflow-hidden border border-slate-200 bg-slate-100 group"
            data-testid={`photo-thumb-${p.id}`}
          >
            <img
              src={p.previewUrl || `${API}/photos/${p.id}`}
              alt={p.filename || "damage"}
              className="w-full h-full object-cover"
            />
            <button
              type="button"
              onClick={() => remove(p.id)}
              className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-slate-900/85 text-white flex items-center justify-center shadow-sm hover:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500"
              aria-label="Remove photo"
              data-testid={`photo-remove-${p.id}`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
        {photos.length < MAX && (
          <button
            type="button"
            onClick={onPick}
            disabled={uploading}
            data-testid="photo-add-btn"
            className="aspect-square rounded-xl border-2 border-dashed border-slate-300 hover:border-amber-500 hover:bg-amber-50/40 transition-colors flex flex-col items-center justify-center text-slate-500 text-xs gap-1 disabled:opacity-50"
          >
            {uploading ? (
              <Loader2 className="w-5 h-5 animate-spin text-amber-600" />
            ) : photos.length === 0 ? (
              <>
                <Camera className="w-5 h-5 text-amber-600" />
                <span>Add photos</span>
              </>
            ) : (
              <>
                <ImagePlus className="w-5 h-5 text-amber-600" />
                <span>+{MAX - photos.length} more</span>
              </>
            )}
          </button>
        )}
      </div>
      <p className="mt-3 text-xs text-slate-500 leading-relaxed">
        Upload up to {MAX} photos showing cracks, dents, liquid damage, or the area experiencing the problem. Photos help us prepare for your appointment but do not replace our in-person device inspection.
      </p>
    </div>
  );
}
